import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  FilterDrawer,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type FilterField,
  type FilterValues,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal, openModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { downloadText, rowsToCsv } from '@repletoryreach/sdk-utils/file';
import type { admin } from '@repletoryreach/sdk-api';
import { INVOICE_LIST_LIMIT, useInvoices, useRegenerateInvoice, useValidateInvoice } from '../../../data/useInvoices';
import { useTenantMap } from '../../../data/useTenants';
import { usePersistedState } from '../../../lib/usePersistedState';
import { InvoicesTable, statusLabel, validationLabel } from './components/InvoicesTable';
import { InvoicesList } from './components/InvoicesList';
import { GenerateInvoiceDrawer } from './components/GenerateInvoiceDrawer';
import { ValidationIssues } from './components/ValidationIssues';
import { openFileViewer } from '@repletoryreach/sdk-ui/file-viewer';
import { invoiceViewerSource } from './components/invoice-source';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { BillingToolbar } from '../components/BillingToolbar';
import { ViewToggle } from '../components/ViewToggle';
import { IconDownload, IconPlus } from '../components/icons';
import { withinRange, type DateRangeKey } from '../components/dateRange';
import { formatDate, formatInteger, formatMinor } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';

type KpiKey = 'all' | admin.InvoiceStatus | 'invalid';

const KPI_STATUSES: admin.InvoiceStatus[] = ['draft', 'issued', 'paid', 'void', 'past_due'];

export default function InvoicesPage() {
  const t = useT();
  const [params] = useSearchParams();
  const { data, isLoading, error, refetch } = useInvoices();
  const { map: tenants } = useTenantMap();
  const regen = useRegenerateInvoice();
  const validate = useValidateInvoice();
  const qc = useQueryClient();

  const [genOpen, setGenOpen] = useState(false);

  const all = useMemo(() => data?.data ?? [], [data]);

  const [view, setView] = usePersistedState<ViewMode>('admin.invoices.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.invoices.pageSize', 25);
  const [query, setQuery] = useState('');
  const [range, setRange] = useState<DateRangeKey>('all');
  // The billing hub links here with `?validation=invalid`.
  const [kpi, setKpi] = useState<KpiKey | null>(params.get('validation') === 'invalid' ? 'invalid' : 'all');
  // Subscriptions and the ledger both link here for one organisation, so the
  // drawer opens already narrowed to it rather than dropping the operator into
  // every invoice on the platform.
  const initialOrg = params.get('org_id') ?? '';
  const [filters, setFilters] = useState<FilterValues>(initialOrg ? { organisation: initialOrg } : {});
  const [page, setPage] = useState(1);

  /**
   * The window the counts describe: the date range, and nothing else. A KPI
   * chip that counted only what its own filter admits would always read the
   * same number as the table under it.
   */
  const inWindow = useMemo(() => {
    const now = Date.now();
    // An invoice that has not been issued has no issue date, so the period end
    // stands in for it: that is the date the cycle it bills actually closed.
    return all.filter((i) => withinRange(i.issued_at ?? i.period_end, range, now));
  }, [all, range]);

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(
      KPI_STATUSES.map((s) => [s, inWindow.filter((i) => i.status === s).length])
    ) as Record<admin.InvoiceStatus, number>;
    return { all: inWindow.length, invalid: inWindow.filter((i) => i.validation_status === 'invalid').length, ...byStatus };
  }, [inWindow]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('billing.invoices.kpi.all'), value: counts.all, tone: 'primary' },
    { id: 'draft', label: statusLabel(t, 'draft'), value: counts.draft, tone: 'inactive' },
    { id: 'issued', label: statusLabel(t, 'issued'), value: counts.issued, tone: 'pending' },
    { id: 'paid', label: statusLabel(t, 'paid'), value: counts.paid, tone: 'healthy' },
    { id: 'past_due', label: statusLabel(t, 'past_due'), value: counts.past_due, tone: 'warning' },
    { id: 'void', label: statusLabel(t, 'void'), value: counts.void, tone: 'neutral' },
    { id: 'invalid', label: t('billing.invoices.kpi.invalid'), value: counts.invalid, tone: 'down' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    const currencies = Array.from(new Set(all.map((i) => i.currency))).sort();
    const orgs = Array.from(new Set(all.map((i) => i.organisation_id)))
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: 'organisation',
        label: t('billing.subscriptions.filters.organisation'),
        placeholder: t('billing.subscriptions.filters.anyOrg'),
        options: orgs
      },
      {
        key: 'currency',
        label: t('common.currency'),
        placeholder: t('billing.ledger.filters.anyCurrency'),
        options: currencies.map((c) => ({ value: c, label: c }))
      },
      {
        key: 'validation',
        label: t('billing.invoices.columns.validation'),
        placeholder: t('billing.invoices.filters.anyValidation'),
        options: (['valid', 'invalid', 'unvalidated', 'skipped'] as const)
          .map((v) => ({ value: v, label: validationLabel(t, v) }))
      }
    ];
  }, [all, tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) || range !== 'all' || (kpi !== null && kpi !== 'all') || drawerActive > 0;
  function clearAllFilters() { setQuery(''); setRange('all'); setKpi('all'); setFilters({}); setPage(1); }

  const rangeLabels: Record<DateRangeKey, string> = {
    all: t('billing.range.all'), today: t('billing.range.today'), '7d': t('billing.range.last7'), '30d': t('billing.range.last30')
  };

  /** One removable pill per narrowing choice, the KPI chip included. */
  const pills: ActiveFilterPill[] = [];
  if (range !== 'all') pills.push({ id: 'range', field: t('billing.invoices.range.issued'), value: rangeLabels[range] });
  if (kpi && kpi !== 'all') {
    pills.push({
      id: 'kpi',
      field: kpi === 'invalid' ? t('billing.invoices.columns.validation') : t('common.status'),
      value: kpi === 'invalid' ? validationLabel(t, 'invalid') : statusLabel(t, kpi)
    });
  }
  if (filters.organisation) pills.push({ id: 'organisation', field: t('billing.subscriptions.filters.organisation'), value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort') });
  if (filters.currency) pills.push({ id: 'currency', field: t('common.currency'), value: filters.currency });
  if (filters.validation) pills.push({ id: 'validation', field: t('billing.invoices.columns.validation'), value: validationLabel(t, filters.validation) });

  function removePill(id: string) {
    setPage(1);
    if (id === 'range') { setRange('all'); return; }
    if (id === 'kpi') { setKpi('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let rows = inWindow;
    if (kpi === 'invalid') rows = rows.filter((i) => i.validation_status === 'invalid');
    else if (kpi && kpi !== 'all') rows = rows.filter((i) => i.status === kpi);
    if (filters.organisation) rows = rows.filter((i) => i.organisation_id === filters.organisation);
    if (filters.currency) rows = rows.filter((i) => i.currency === filters.currency);
    if (filters.validation) rows = rows.filter((i) => i.validation_status === filters.validation);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (i) =>
          i.invoice_number.toLowerCase().includes(q) ||
          (tenants.get(i.organisation_id)?.name ?? '').toLowerCase().includes(q) ||
          (tenants.get(i.organisation_id)?.slug ?? '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [inWindow, kpi, filters, query, tenants]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  // The PDF opens in the file viewer rather than landing in the downloads
  // folder: reading an invoice is the common case, and the viewer carries
  // Download and Print for the other one.
  function openInvoice(r: admin.AdminInvoice) {
    openFileViewer(invoiceViewerSource(r, t, tenants.get(r.organisation_id)?.name));
  }

  async function runValidation(r: admin.AdminInvoice) {
    try {
      const res = await validate.mutateAsync(r.id);
      const verdict = res.data.validation;
      if (verdict.valid && verdict.issues.length === 0) {
        notifySuccess(
          t('billing.invoices.toast.validated', { number: r.invoice_number }),
          t('billing.invoices.toast.validatedTitle')
        );
        return;
      }
      // An invalid invoice is a compliance problem with named causes, so the
      // issues are shown rather than summarised into a single toast line.
      openModal({
        title: t('billing.invoices.modal.validate.title', { number: r.invoice_number }),
        children: <ValidationIssues issues={verdict.issues} validator={verdict.validator} />
      });
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.invoices.toast.validateFailed'));
    }
  }

  function confirmRegenerate(r: admin.AdminInvoice) {
    openConfirmModal({
      title: t('billing.invoices.modal.regenerate.title', { number: r.invoice_number }),
      children: <p style={{ margin: 0 }}>{t('billing.invoices.modal.regenerate.body', { number: r.invoice_number })}</p>,
      labels: { confirm: t('billing.invoices.regenerate'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          await regen.mutateAsync(r.id);
          notifySuccess(
            t('billing.invoices.toast.regenQueued', { number: r.invoice_number }),
            t('billing.invoices.toast.regenQueuedTitle')
          );
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.invoices.toast.regenFailed'));
        }
      }
    });
  }

  /**
   * The export carries the organisation's name and formatted money next to the
   * stored minor-unit integers, because the file is opened by a person in a
   * spreadsheet and reconciled against the same figures the API returns.
   */
  function exportCsv() {
    const rows = filtered.map((r) => ({
      invoice_number: r.invoice_number,
      organisation: tenants.get(r.organisation_id)?.name ?? '',
      organisation_id: r.organisation_id,
      period_start: formatDate(r.period_start),
      period_end: formatDate(r.period_end),
      issued_at: r.issued_at ?? '',
      due_at: r.due_at ?? '',
      subtotal: formatMinor(r.subtotal_minor, r.currency),
      vat: formatMinor(r.vat_minor, r.currency),
      total: formatMinor(r.total_minor, r.currency),
      total_minor: r.total_minor,
      currency: r.currency,
      status: r.status,
      validation_status: r.validation_status,
      validator: r.validation_validator ?? ''
    }));
    const headers = [
      'invoice_number', 'organisation', 'organisation_id', 'period_start', 'period_end',
      'issued_at', 'due_at', 'subtotal', 'vat', 'total', 'total_minor', 'currency',
      'status', 'validation_status', 'validator'
    ];
    downloadText(rowsToCsv(rows, { headers, bom: true }), `invoices-${Date.now()}.csv`, 'text/csv');
    notifySuccess(t('billing.invoices.toast.exported', { count: rows.length }), t('billing.invoices.toast.exportedTitle'));
  }

  const capped = all.length >= INVOICE_LIST_LIMIT;
  const totalLabel = capped
    ? t('billing.invoices.showingNewest', { limit: formatInteger(INVOICE_LIST_LIMIT) })
    : t('billing.invoices.showingCount', { shown: formatInteger(filtered.length), total: formatInteger(all.length) });

  const rowActions = {
    onView: openInvoice,
    onValidate: (r: admin.AdminInvoice) => { void runValidation(r); },
    onRegenerate: confirmRegenerate
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.invoices.eyebrow')}
        title={t('billing.invoices.title')}
        description={t('billing.invoices.description')}
        actions={
          <Group gap="sm" wrap="nowrap">
            <Button size="sm" variant="outline" leftSection={<IconDownload />} disabled={filtered.length === 0} onClick={exportCsv}>
              {t('action.exportCsv')}
            </Button>
            <Button size="sm" leftSection={<IconPlus />} onClick={() => setGenOpen(true)}>{t('billing.invoices.generate')}</Button>
            <ViewToggle value={view} onChange={setView} />
          </Group>
        }
      />

      <GenerateInvoiceDrawer
        opened={genOpen}
        onClose={() => setGenOpen(false)}
        onGenerated={() => qc.invalidateQueries({ queryKey: ['admin', 'invoices'] })}
      />

      {counts.invalid > 0 && (
        <Alert color="warning" title={t('billing.invoices.invalidBanner.title', { count: counts.invalid })}>
          {t('billing.invoices.invalidBanner.body')}
        </Alert>
      )}

      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />

      <BillingToolbar
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('billing.invoices.search')}
        range={range}
        onRangeChange={(next) => { setRange(next); setPage(1); }}
      >
        <FilterDrawer
          fields={filterFields}
          values={filters}
          onApply={(next) => { setFilters(next); setPage(1); }}
          onClear={clearAllFilters}
        />
      </BillingToolbar>

      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        totalLabel={totalLabel}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={t('billing.invoices.empty')}
            description={hasActiveFilters ? t('common.tryClearingFilters') : t('billing.invoices.emptyHint')}
            action={
              hasActiveFilters
                ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button>
                : <Button size="xs" onClick={() => setGenOpen(true)}>{t('billing.invoices.generate')}</Button>
            }
          />
        }
        error={
          error ? (
            <Alert color="error">
              <Stack gap="sm">
                <Text role="bodySmall">{t('billing.invoices.loadError')}</Text>
                <Group>
                  <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            capped={capped}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        {view === 'table' && <InvoicesTable records={paged} loading={isLoading} {...rowActions} />}
        {view === 'list' && <InvoicesList records={paged} {...rowActions} />}
      </TableShell>

      <Alert color="info">{t('billing.invoices.footnote')}</Alert>
    </Stack>
  );
}
